import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Sparkles, Loader2, FileText, GitCompare, ClipboardList } from "lucide-react";

const AI_BG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrfGVufDB8fHx8MTc4Mzc5ODY2NXww&ixlib=rb-4.1.0&q=85";

const TYPE_META = {
  resume_summary: { label: "Resume Summary", icon: FileText },
  candidate_match: { label: "Candidate Match", icon: GitCompare },
  search_analysis: { label: "Search Analysis", icon: ClipboardList },
};

const TABS = [
  { key: "", label: "All" },
  { key: "resume_summary", label: "Resume Summaries" },
  { key: "candidate_match", label: "Candidate Matching" },
  { key: "search_analysis", label: "Search Analysis" },
];

export default function AIWorkspace() {
  const [tab, setTab] = useState("");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setItems(null);
    api.get("/ai/artifacts", { params: tab ? { artifact_type: tab } : {} })
      .then(({ data }) => { setItems(data); setActive(data[0] || null); });
  }, [tab]);

  return (
    <div>
      <PageHeader overline="Internal only · Claude Sonnet 4.6" title="AI Workspace" />

      {/* Hero strip */}
      <div className="relative h-32 border-b border-black/10 overflow-hidden">
        <img src={AI_BG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent flex items-center px-8">
          <div>
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-primary" /><span className="overline">Recruiter intelligence</span></div>
            <p className="font-heading font-bold text-2xl max-w-xl">Every AI artifact is internal-only and never exposed to clients or candidates.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-8 border-b border-black/10 flex gap-6 bg-white">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`ai-tab-${t.key || "all"}`} onClick={() => setTab(t.key)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-[#09090B]" : "border-transparent text-muted-foreground hover:text-[#09090B]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-8">
        {!items && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {items && items.length === 0 && <Empty icon={Sparkles} title="No AI artifacts yet" hint="Generate resume summaries from candidate profiles, or run matching & brief analysis from search pages." />}
        {items && items.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            <div className="lg:col-span-1 bg-white max-h-[70vh] overflow-y-auto">
              {items.map((a) => {
                const m = TYPE_META[a.artifact_type] || { label: a.artifact_type, icon: Sparkles };
                const Icon = m.icon;
                return (
                  <button key={a.id} onClick={() => setActive(a)}
                    className={`w-full text-left p-4 border-b border-black/5 hover:bg-black/5 transition-colors flex gap-3 ${active?.id === a.id ? "bg-black/10" : ""}`}>
                    <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{m.label}</p>
                      <p className="overline text-[0.5rem] mt-1">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="lg:col-span-2 bg-white p-6">
              {active ? (
                <>
                  <p className="overline mb-4">{(TYPE_META[active.artifact_type] || {}).label || active.artifact_type}</p>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-[#27272A] leading-relaxed border-l-2 border-primary pl-4">{active.output_data}</pre>
                </>
              ) : <p className="text-sm text-muted-foreground">Select an artifact.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
